import ParticleLoader from "../../Components/ParticleLoader";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import {
    CheckCircleIcon,
    DocumentCheckIcon,
    LockClosedIcon,
    ShieldCheckIcon,
    VideoCameraIcon,
} from "@heroicons/react/24/outline";
import QuestionInput from "../../Components/Questionnaire/QuestionInput";
import useQuestionnaireRecording from "../../hooks/useQuestionnaireRecording";
import {
    getPublicQuestionnaire,
    checkQuestionnairePassword,
    startQuestionnaireResponse,
    submitAnswer,
    completeQuestionnaireResponse,
} from "../../Requests/QuestionnaireRequests";
import { questionnaireError } from "../../Components/Questionnaire/questionnaireErrors";

/**
 * Public answering page.
 *
 * Stage order is deliberate: consent is signed BEFORE screen/camera permission
 * is requested, because the consent text is what discloses the recording.
 * Asking for the camera first would be collecting under a consent the
 * respondent has not yet read.
 */
const STAGES = {
    LOADING: "loading",
    PASSWORD: "password",
    CONSENT: "consent",
    PERMISSIONS: "permissions",
    QUESTIONS: "questions",
    DONE: "done",
    ERROR: "error",
};

export default function PublicQuestionnaire() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const recording = useQuestionnaireRecording();

    const [stage, setStage] = useState(STAGES.LOADING);
    const [questionnaire, setQuestionnaire] = useState(null);
    const [error, setError] = useState(null);
    const [password, setPassword] = useState("");
    const [signerName, setSignerName] = useState("");
    const [email, setEmail] = useState("");
    const [consentChecked, setConsentChecked] = useState(false);
    const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
    const signaturePad = useRef(null);
    const [responseUuid, setResponseUuid] = useState(null);
    const [answers, setAnswers] = useState({});
    const [index, setIndex] = useState(0);
    const [busy, setBusy] = useState(false);

    // Once per link. `t` is deliberately not a dependency: switching the
    // language below rebinds it, and refetching on that would loop.
    useEffect(() => {
        getPublicQuestionnaire(token)
            .then((res) => {
                setQuestionnaire(res.data);
                setStage(res.data.is_password_protected ? STAGES.PASSWORD : STAGES.CONSENT);
            })
            .catch(() => {
                setError(t("PublicQuestionnaire.NotFound", "This questionnaire is not available."));
                setStage(STAGES.ERROR);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    /**
     * The form is shown in the language the researcher chose for it, not the
     * one the respondent's browser happens to prefer. Restored on unmount so a
     * researcher previewing their own link does not end up with the whole app
     * switched to the questionnaire's language.
     *
     * Kept apart from the fetch on purpose: this effect changes what `t` is, so
     * anything that both depends on `t` and calls changeLanguage feeds itself.
     */
    const language = questionnaire?.language;

    useEffect(() => {
        if (!language) return undefined;

        const previousLanguage = i18n.language;
        i18n.changeLanguage(language);

        return () => {
            if (previousLanguage) i18n.changeLanguage(previousLanguage);
        };
    }, [language, i18n]);

    const questions = useMemo(() => questionnaire?.questions || [], [questionnaire]);
    const current = questions[index];
    // Screen/camera recording temporarily disabled (also commented out in QuestionnaireBuilderModal.js).
    // const wantsRecording = questionnaire?.record_screen || questionnaire?.record_camera;
    const wantsRecording = false;

    const handlePassword = async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
            const res = await checkQuestionnairePassword(token, password);
            if (res.data.valid) {
                setStage(STAGES.CONSENT);
                setError(null);
            } else {
                setError(t("PublicQuestionnaire.WrongPassword", "Incorrect password."));
            }
        } finally {
            setBusy(false);
        }
    };

    /** Signing consent is what opens the response; there is no other entry point. */
    const handleSignConsent = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const res = await startQuestionnaireResponse(token, {
                consent_agreed: true,
                consent_signer_name: signerName,
                // Trimmed of the empty canvas around the stroke, so what is
                // stored is the mark itself rather than a mostly blank image.
                consent_signature: signaturePad.current
                    .getTrimmedCanvas()
                    .toDataURL("image/png"),
                email: email || null,
                password: password || null,
            });
            setResponseUuid(res.data.response_uuid);
            setStage(wantsRecording ? STAGES.PERMISSIONS : STAGES.QUESTIONS);
        } catch (e) {
            setError(questionnaireError(e, t, "PublicQuestionnaire.CouldNotStart"));
        } finally {
            setBusy(false);
        }
    };

    const handlePermissions = async () => {
        setBusy(true);
        try {
            await recording.start(responseUuid, {
                wantScreen: !!questionnaire.record_screen,
                wantCamera: !!questionnaire.record_camera,
            });
        } finally {
            setBusy(false);
            // Declining is allowed: the questionnaire still runs, just unrecorded.
            setStage(STAGES.QUESTIONS);
        }
    };

    const setAnswer = useCallback((value) => {
        setAnswers((prev) => ({ ...prev, [current.uuid]: value }));
    }, [current]);

    const hasAnswer = (question) => {
        const value = answers[question.uuid];
        if (value === undefined || value === null || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        // A voice answer streams to the server while it records, so by the time
        // it is in state it is already stored — there is nothing left to upload.
        if (question.type === "voice") return !!value.uploaded;
        return true;
    };

    const goNext = async () => {
        if (!current) return;

        const answerable = current.type !== "image_only" && current.type !== "video_only";

        if (answerable && current.required && !hasAnswer(current)) {
            setError(t("PublicQuestionnaire.Required", "Please answer this question."));
            return;
        }

        setBusy(true);
        setError(null);

        try {
            if (answerable && current.type === "voice") {
                // Nothing to upload: the recorder streamed the answer as it was
                // spoken and finalised it on stop. Only the skip case needs a call.
                if (!answers[current.uuid]?.uploaded) {
                    await submitAnswer(responseUuid, {
                        question_uuid: current.uuid,
                        value: null,
                        skipped: true,
                    });
                }
            } else if (answerable) {
                await submitAnswer(responseUuid, {
                    question_uuid: current.uuid,
                    value: answers[current.uuid] ?? null,
                    skipped: !hasAnswer(current),
                });
            }

            if (index + 1 < questions.length) {
                setIndex(index + 1);
            } else {
                await completeQuestionnaireResponse(responseUuid);
                await recording.stop();
                setStage(STAGES.DONE);
            }
        } catch (e) {
            setError(questionnaireError(e, t, "PublicQuestionnaire.CouldNotSave"));
        } finally {
            setBusy(false);
        }
    };

    const shell = (children) => (
        <div className="min-h-screen bg-gray-50 py-10 px-4">
            <div className="mx-auto max-w-2xl rounded-xl bg-white p-8 shadow-sm">{children}</div>
        </div>
    );

    if (stage === STAGES.LOADING) {
        return shell(<div className="flex justify-center"><ParticleLoader size={48} /></div>);
    }

    if (stage === STAGES.ERROR) {
        return shell(<p className="text-center text-red-600">{error}</p>);
    }

    if (stage === STAGES.PASSWORD) {
        return shell(
            <form onSubmit={handlePassword} className="space-y-4">
                <div className="flex items-center gap-2 text-gray-900">
                    <LockClosedIcon className="h-5 w-5" />
                    <h1 className="text-lg font-semibold">{questionnaire.title}</h1>
                </div>
                <p className="text-sm text-gray-600">
                    {t("PublicQuestionnaire.PasswordPrompt", "This questionnaire is password protected.")}
                </p>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black"
                    required
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                    type="submit"
                    disabled={busy}
                    className="w-full rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                >
                    {t("Generic.Continue", "Continue")}
                </button>
            </form>
        );
    }

    if (stage === STAGES.CONSENT) {
        return shell(
            <form onSubmit={handleSignConsent} className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">{questionnaire.title}</h1>
                    {questionnaire.welcome_message && (
                        <p className="mt-2 whitespace-pre-line text-sm text-gray-600">
                            {questionnaire.welcome_message}
                        </p>
                    )}
                </div>

                <div className="rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
                        <ShieldCheckIcon className="h-5 w-5 text-gray-500" />
                        <h2 className="text-sm font-semibold text-gray-900">
                            {t("PublicQuestionnaire.ConsentTitle", "Informed consent")}
                        </h2>
                    </div>
                    <div className="max-h-72 overflow-y-auto px-4 py-4">
                        {questionnaire.consent?.content && (
                            <div className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                                {questionnaire.consent.content}
                            </div>
                        )}
                        {questionnaire.consent?.document_url && (
                            <a
                                href={questionnaire.consent.document_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-block text-sm font-medium text-gray-900 underline"
                            >
                                {questionnaire.consent.document_name ||
                                    t("PublicQuestionnaire.ConsentDocument", "Read the consent document")}
                            </a>
                        )}
                    </div>
                </div>

                {questionnaire.ethics && (
                    <div className="rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
                            <DocumentCheckIcon className="h-5 w-5 text-gray-500" />
                            <h2 className="text-sm font-semibold text-gray-900">
                                {t("PublicQuestionnaire.EthicsTitle", "Ethics committee opinion")}
                            </h2>
                        </div>
                        <div className="max-h-48 overflow-y-auto px-4 py-4">
                            {questionnaire.ethics.content && (
                                <div className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                                    {questionnaire.ethics.content}
                                </div>
                            )}
                            {questionnaire.ethics.document_url && (
                                <a
                                    href={questionnaire.ethics.document_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-3 inline-block text-sm font-medium text-gray-900 underline"
                                >
                                    {questionnaire.ethics.document_name ||
                                        t("PublicQuestionnaire.EthicsDocument", "Read the ethics committee opinion")}
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {wantsRecording && (
                    <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <VideoCameraIcon className="mt-0.5 h-5 w-5 flex-none" />
                        <span>
                            {t(
                                "PublicQuestionnaire.RecordingNotice",
                                "This study records your screen and/or camera while you answer. You will be asked for permission after signing."
                            )}
                        </span>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            {t("PublicQuestionnaire.SignatureLabel", "Type your full name to sign")}
                        </label>
                        <input
                            type="text"
                            required
                            value={signerName}
                            onChange={(e) => setSignerName(e.target.value)}
                            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black"
                        />
                    </div>

                    <div>
                        <div className="flex items-baseline justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                                {t("PublicQuestionnaire.DrawSignatureLabel", "Draw your signature")}
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    signaturePad.current?.clear();
                                    setHasDrawnSignature(false);
                                }}
                                className="text-sm font-medium text-gray-500 hover:text-gray-900"
                            >
                                {t("PublicQuestionnaire.ClearSignature", "Clear")}
                            </button>
                        </div>
                        <div className="mt-1 rounded-md border border-gray-300 bg-white">
                            <SignatureCanvas
                                ref={signaturePad}
                                penColor="#111827"
                                // A viewport resize — a mobile keyboard opening, say —
                                // must not wipe a signature already drawn.
                                clearOnResize={false}
                                onEnd={() => setHasDrawnSignature(true)}
                                canvasProps={{ className: "h-40 w-full touch-none" }}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {t(
                                "PublicQuestionnaire.SignatureRecordNote",
                                "Your signature is stored with the date and time, your name, the exact text you agreed to, and your IP address, as the record of your consent."
                            )}
                        </p>
                    </div>

                    {questionnaire.collect_email && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t("PublicQuestionnaire.EmailLabel", "Your email")}
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black"
                            />
                        </div>
                    )}

                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(e) => setConsentChecked(e.target.checked)}
                            className="mt-1 rounded text-black focus:ring-black"
                        />
                        <span className="text-sm text-gray-700">
                            {t(
                                "PublicQuestionnaire.ConsentAgree",
                                "I have read and understood the information above, and I agree to take part."
                            )}
                        </span>
                    </label>
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <button
                    type="submit"
                    disabled={busy || !consentChecked || !signerName.trim() || !hasDrawnSignature}
                    className="w-full rounded-md bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                >
                    {t("PublicQuestionnaire.SignAndStart", "Sign and start")}
                </button>

                {/* Attribution required by the licences of the packages that
                    draw the signature pad above. */}
                <p className="text-xs text-gray-400">
                    {t("PublicQuestionnaire.SignatureCredit", "Signature capture by react-signature-canvas and trim-canvas (Apache-2.0, © 2016 Anton Gilgur) and signature_pad (MIT, © 2017 Szymon Nowak).")}{" "}
                    <a
                        href="/licenses/signature-capture.txt"
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-gray-600"
                    >
                        {t("PublicQuestionnaire.SignatureCreditLink", "Licences")}
                    </a>
                </p>
            </form>
        );
    }

    if (stage === STAGES.PERMISSIONS) {
        return shell(
            <div className="space-y-5 text-center">
                <VideoCameraIcon className="mx-auto h-10 w-10 text-gray-900" />
                <h1 className="text-xl font-semibold text-gray-900">
                    {t("PublicQuestionnaire.PermissionsTitle", "Allow recording")}
                </h1>
                <p className="text-sm text-gray-600">
                    {t(
                        "PublicQuestionnaire.PermissionsBody",
                        "Your browser will ask permission to record. You consented to this on the previous screen; you can still decline and continue without recording."
                    )}
                </p>
                <div className="flex justify-center gap-3">
                    <button
                        type="button"
                        onClick={() => setStage(STAGES.QUESTIONS)}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        {t("PublicQuestionnaire.SkipRecording", "Continue without recording")}
                    </button>
                    <button
                        type="button"
                        onClick={handlePermissions}
                        disabled={busy}
                        className="rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                        {t("PublicQuestionnaire.AllowRecording", "Allow and continue")}
                    </button>
                </div>
            </div>
        );
    }

    if (stage === STAGES.DONE) {
        return shell(
            <div className="space-y-4 text-center">
                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
                <h1 className="text-xl font-semibold text-gray-900">
                    {t("PublicQuestionnaire.ThankYou", "Thank you")}
                </h1>
                {questionnaire.thank_you_message && (
                    <p className="whitespace-pre-line text-sm text-gray-600">
                        {questionnaire.thank_you_message}
                    </p>
                )}
            </div>
        );
    }

    return shell(
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                    {t("PublicQuestionnaire.Progress", "Question {{n}} of {{total}}", {
                        n: index + 1,
                        total: questions.length,
                    })}
                </span>
                {(recording.screenEnabled || recording.cameraEnabled) && (
                    <span className="flex items-center gap-1.5 text-red-600">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" />
                        {t("PublicQuestionnaire.Recording", "Recording")}
                    </span>
                )}
            </div>

            <div className="h-1 w-full rounded-full bg-gray-200">
                <div
                    className="h-1 rounded-full bg-black transition-all"
                    style={{ width: `${((index + 1) / questions.length) * 100}%` }}
                />
            </div>

            {recording.status === "degraded" && recording.error && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">{recording.error}</p>
            )}

            <div>
                <h2 className="text-lg font-medium text-gray-900">{current?.question_text}</h2>
                {current?.description && (
                    <p className="mt-1 text-sm text-gray-500">{current.description}</p>
                )}
            </div>

            <QuestionInput
                question={current}
                value={answers[current?.uuid]}
                onChange={setAnswer}
                disabled={busy}
                responseUuid={responseUuid}
            />

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
                type="button"
                onClick={goNext}
                disabled={busy}
                className="w-full rounded-md bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
                {index + 1 < questions.length
                    ? t("Generic.Next", "Next")
                    : t("PublicQuestionnaire.Submit", "Submit")}
            </button>
        </div>
    );
}
